import { HttpParams } from '@angular/common/http'

export const httpParams = (...fns: (() => HttpParams)[]): HttpParams => {
  return fns
    .map((fn) => fn())
    .reduce(
      (acc1, params) =>
        params
          .keys()
          .reduce(
            (acc2, key) =>
              (params.getAll(key) ?? []).reduce(
                (acc3, value) => acc3.append(key, value),
                acc2,
              ),
            acc1,
          ),
      new HttpParams(),
    )
}

export const withHttpParams =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <T extends (...args: readonly any[]) => HttpParams>(fn: T, ...params: Parameters<T>) =>
    (): HttpParams =>
      fn(...params)

// Pagination
export type PaginationParams = Readonly<{
  pageSize: number
  pageNumber: number
}>

export const pagination = (
  params: PaginationParams = {
    pageNumber: 1,
    pageSize: 1000,
  },
): HttpParams =>
  httpParams(
    () => new HttpParams().append('limit', params.pageSize),
    () => new HttpParams().append('start', (params.pageSize * Math.max((params.pageNumber - 1), 0) + 1)),
  )

export const filterOperators = [
  '==',
  '>',
  '>=',
  '<',
  '<=',
  '!=',
  'between=',
  'in=',
  'splike=',
] as const

// Filters
export type FilterOperation = typeof filterOperators[number]

export type FilterParamValue = Readonly<{ value: string, operation?: FilterOperation }>

export type FilterParams = Readonly<{
  name: string
  values: readonly FilterParamValue[]
}>

export const filter = (params: readonly FilterParams[] = []): HttpParams =>
  httpParams(
    ...params.map(
      (p) => () =>
        httpParams(
          ...p.values.map(
            (v) => () =>
              v.value
                ? new HttpParams().append(
                  p.name,
                  v.operation && v.operation !== '=='
                    ? `${v.operation}${v.value}`
                    : `${v.value}`,
                )
                : new HttpParams(),
          ),
        ),
    ),
  )

// Sorting
export type SortDirection = 'asc' | 'desc'

export type SortParams = Readonly<{
  name: string
  direction?: SortDirection
}>

export const sort = (params: readonly SortParams[] = []): HttpParams =>
  params.length
    ? httpParams(() =>
      new HttpParams().append(
        'order_by',
        params.map(p => `${p.direction === 'desc' ? '-' : ''}${p.name}`).join(','),
      ),
    )
    : httpParams()
